// *** Angular
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { first } from 'rxjs/operators';

// *** Services
import { AdministratorStatisticsService } from 'src/app/services/administrator-statistics.service';
import { CsvExportService } from 'src/app/services/csv-export.service';
import { ExcelExportService } from 'src/app/services/excel-export.service';
import { PdfExportService } from 'src/app/services/pdf-export.service';
import { SubmissionsService } from 'src/app/services/submissions.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
   selector: 'app-administrator-statistics',
   templateUrl: './administrator-statistics.component.html',
   styleUrls: ['./administrator-statistics.component.scss'],
})
export class AdministratorStatisticsComponent implements OnInit {
   /**
    * Variabel used to store institutiton statistics (unfiltered - for institutional metrics section)
    */
   institutionData: any;
   /**
    * Variable used to store filtered statistics (for similarity scores and charts below risk trends)
    */
   filteredData: any;
   /**
    * Variable used to store number that changes by function and has a part on showing a specific chart a time
    */
   graphToDisplay: number = 1;
   /**
    * Variable used to store number of assigments of professors within institution
    */
   assignmentsStatistics;
   /**
    *Variable used to store number of assigments of submissions within institution
    */
   submissionsStatistics;
   /**
    *Variable used to store number of assigments of thesis within institution
    */
   thesisStatistics;

   @ViewChild('contentToCapture') contentToCapture: ElementRef;

   // Dynamic threshold values from institutional settings
   mediumRiskThreshold: number = 5;
   highRiskThreshold: number = 20;

   public chartOptions = {
      series: [
         { name: '80% - 100%', data: Array(12).fill(0) },
         { name: '20% - 80%', data: Array(12).fill(0) },
         { name: '0% - 20%', data: Array(12).fill(0) }
      ],
      chart: {
         type: 'area',
         height: 320,
         width: '100%',
         toolbar: { show: true },
         zoom: { enabled: true }
      },
      colors: ['#E74C3C', '#F4B400', '#43B77A'],
      stroke: {
         width: 3,
         curve: 'smooth'
      },
      dataLabels: { enabled: false },
      xaxis: {
         categories: [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
         ],
         labels: { style: { fontSize: '13px', colors: '#888' } }
      },
      yaxis: {
         min: 0,
         max: 100,
         tickAmount: 6,
         labels: { show: true, style: { fontSize: '13px', colors: '#888' }, formatter: (val) => val.toFixed(0) }
      },
      legend: { show: true, position: 'bottom', horizontalAlign: 'center', fontSize: '13px' },
      tooltip: { y: { formatter: (val) => val } },
      grid: {
         borderColor: '#e5e7eb',
         row: { colors: ['#fff', 'transparent'], opacity: 0.5 },
         xaxis: { lines: { show: true } },
         yaxis: { lines: { show: true } }
      },
      fill: { type: 'solid', colors: ['#E74C3C', '#F4B400', '#43B77A'] },
      responsive: [
         { breakpoint: 700, options: { chart: { height: 200 } } }
      ]
   };
   selectedYear: number = new Date().getFullYear();
   availableYears: number[] = [];
   selectedSubmissionType: string = 'student';
   isLoading = false;
   errorMessage = '';
   highRiskCount = 0;
   mediumRiskCount = 0;
   lowRiskCount = 0;
   inProcessCount = 0;
   originalLanguageData: { label: string, value: number, color: string }[] = [];
   originalLanguageTotal: number = 0;
   translatedLanguageData: { label: string, value: number, color: string }[] = [];
   translatedLanguageTotal: number = 0;
   languageColors: string[] = [
      '#E74C3C', '#F4B400', '#43B77A', '#4285F4', '#8E44AD', '#00B8D9', '#FF6F00', '#00C853', '#D50000', '#6D4C41'
   ];
   user: any;
   institutionSettings: any;
   userInterfaceDisplay: string;
   originalityRiskFactorHighRisk: number;
   originalityRiskFactorMediumRisk: number;
   languageMap = {
      'en': 'English',
      'sq': 'Albanian',
      'de': 'German',
      'it': 'Italian',
      'fr': 'French',
      'es': 'Spanish',
      'el': 'Greek',
      'cs': 'Czech',
      'tr': 'Turkish',
      'sk': 'Slovak',
      'lt': 'Lithuanian',
      'lv': 'Latvian',
      'pl': 'Polish',
      'sr': 'Serbian',
      'mk': 'Macedonian',
      'pt': 'Portuguese',
      'nl': 'Dutch',
      'ru': 'Russian',
      'bg': 'Bulgarian',
      'hu': 'Hungarian',
      'ro': 'Romanian',
      'sl': 'Slovenian',
      'sv': 'Swedish',
      'fi': 'Finnish',
      'hr': 'Croatian',
      'bs': 'Bosnian',
      'no': 'Norwegian',
      'da': 'Danish',
      'et': 'Estonian'
   };
   languageDistributionData: any = {};
   aiDetectionData: any = {};
   manipulationData: any = {};
   riskTrendsData: any = {};
   userRegistrationTrendsData: any = {};

   constructor(
      private statistics: AdministratorStatisticsService,
      private csvExportService: CsvExportService,
      private excelExportService: ExcelExportService,
      private pdfExportService: PdfExportService,
      private spinner: NgxSpinnerService,
      private submissionsService: SubmissionsService,
      private translate: TranslateService
   ) { }

   ngOnInit(): void {
      this.user = JSON.parse(localStorage.getItem('user'));
      const institutionSettings = this.user?.Institution?.InstitutionSettings?.[0];
      this.institutionSettings = institutionSettings;
      this.userInterfaceDisplay = institutionSettings?.userInterfaceDisplay;
      this.originalityRiskFactorHighRisk = institutionSettings?.originalityRiskFactorHighRisk;
      this.originalityRiskFactorMediumRisk = institutionSettings?.originalityRiskFactorMediumRisk;
      this.statistics
         .institutionStatistics()
         .pipe(first())
         .subscribe(
            (data) => {
               this.institutionData = data.data;
            },
            (error) => {
               console.log('error', error);
            }
         );

      this.fetchAllDashboardData();

      this.statistics
         .institutionAssignments()
         .pipe(first())
         .subscribe(
            (data) => {
               this.assignmentsStatistics = data.professorAssignments;
            },
            (error) => {
               console.log('error', error);
            }
         );

      this.statistics
         .institutionSubmissions(9)
         .pipe(first())
         .subscribe(
            (data) => {
               this.submissionsStatistics = data.submissions;
               this.aggregateLanguageData();
            },
            (error) => {
               console.log('error', error);
            }
         );

      this.statistics
         .institutionThesis('bachelor')
         .pipe(first())
         .subscribe(
            (data) => {
               this.thesisStatistics = data.thesis;
            },
            (error) => {
               console.log('error', error);
            }
         );

      // Set available years (e.g., last 5 years)
      const currentYear = new Date().getFullYear();
      this.availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i).reverse();
      this.selectedYear = currentYear;
      this.selectedSubmissionType = 'student';
      this.fetchRiskTrends(this.selectedYear, this.selectedSubmissionType);
      this.fetchFilteredData(this.selectedSubmissionType);
   }

   onYearChange(event: any) {
      this.fetchAllDashboardData();
      this.fetchRiskTrends(this.selectedYear, this.selectedSubmissionType);
   }

   onSubmissionTypeChange(event: any) {
      this.fetchAllDashboardData();
      this.fetchRiskTrends(this.selectedYear, this.selectedSubmissionType);
      this.fetchFilteredData(this.selectedSubmissionType);
   }

   fetchFilteredData(type: string) {
      this.statistics
         .institutionStatisticsFiltered(type)
         .pipe(first())
         .subscribe(
            (data) => {
               this.filteredData = data.data;
               console.log('filteredData', this.filteredData);
            },
            (error) => {
               console.log('error fetching filtered data', error);
            }
         );
   }

   fetchRiskTrends(year: number, type: string = 'student') {
      this.isLoading = true;
      this.errorMessage = '';
      let obs;
      if (type === 'student') {
         obs = this.statistics.institutionSubmissionsStatistics(year);
      } else {
         obs = this.statistics.institutionSubmissionsStatisticsBasic(year);
      }
      obs.pipe(first()).subscribe(
         (data) => {
            this.chartOptions = {
               series: [
                  {
                     name: '80% - 100%',
                     data: data.highSimilarity || Array(12).fill(0)
                  },
                  {
                     name: '20% - 80%',
                     data: data.mediumSimilarity || Array(12).fill(0)
                  },
                  {
                     name: '0% - 20%',
                     data: data.lowSimilarity || Array(12).fill(0)
                  }
               ],
               chart: {
                  type: 'line',
                  height: 320,
                  width: '100%',
                  toolbar: { show: false },
                  zoom: { enabled: false }
               },
               colors: ['#E74C3C', '#F4B400', '#43B77A'],
               stroke: {
                  width: 3,
                  curve: 'smooth'
               },
               dataLabels: {
                  enabled: false
               },
               xaxis: {
                  categories: [
                     'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
                  ],
                  labels: {
                     style: { fontSize: '13px', colors: '#888' },
                  },
               },
               yaxis: {
                  min: 0,
                  max: 60,
                  tickAmount: 6,
                  labels: {
                     style: { fontSize: '13px', colors: '#888' },
                     formatter: (val) => val.toFixed(0),
                     show: false
                  }
               },
               legend: {
                  show: false,
                  position: 'bottom',
                  horizontalAlign: 'center',
                  fontSize: '13px',
               },
               tooltip: {
                  y: { formatter: (val) => val }
               },
               grid: {
                  borderColor: '#e5e7eb',
                  row: { colors: ['#fff', 'transparent'], opacity: 0.5 },
                  xaxis: {
                     lines: { show: true }
                  },
                  yaxis: {
                     lines: { show: true }
                  }
               },
               fill: { type: 'solid', colors: ['#E74C3C', '#F4B400', '#43B77A'] },

               responsive: [
                  {
                     breakpoint: 700,
                     options: { chart: { height: 200 } }
                  }
               ]
            };
            this.isLoading = false;
         },
         (error) => {
            this.chartOptions = null;
            this.errorMessage = 'Failed to load chart data.';
            this.isLoading = false;
         }
      );
   }

   /**
    * Method used to change the statistics of submissions by professors or students.
    * @param event
    */
   changeType(event) {
      this.statistics
         .institutionSubmissions(event)
         .pipe(first())
         .subscribe(
            (data) => {
               this.submissionsStatistics = data.submissions;
            },
            (error) => {
               console.log('error', error);
            }
         );
   }

   /**
    * Method used to change the statistics of thesis by types (Bachelor, Master or Ph.D).
    * @param event
    */
   changeThesisType(event) {
      this.statistics
         .institutionThesis(event)
         .pipe(first())
         .subscribe(
            (data) => {
               this.thesisStatistics = data.thesis;
            },
            (error) => {
               console.log('error', error);
            }
         );
   }
   /**
    * Function is used to change which chart will be shown
    * @param value
    */
   filter(value) {
      value = parseInt(value);
      this.graphToDisplay = value;
   }

   prepareExportData() {
      const data = [
         ["Institutional Metrics"],
         [""],
         ["Type", "Total"],
         ["Subjects", this.institutionData?.nrOfSubjects || 0],
         ["Assignments", this.institutionData?.nrOfAssignments || 0],
         ["Educators", this.institutionData?.nrOfProfessors || 0],
         ["Students", this.institutionData?.nrOfStudents || 0],
         ["Student Submissions", this.institutionData?.nrOfStudentSubmissions || 0],
         ["Student Precheck Submissions", this.institutionData?.nrOfStudentPreCheckSubmissions || 0],
         ["Educator Submissions", this.institutionData?.nrOfProfessorSubmissions || 0],
         ["Deleted Submissions", this.institutionData?.deletedSubmissions || 0],
         [""],

         ["Originality Report Insights"],
         [""],
         ["Score Breakdown"],
         [""],
         [this.getHighRiskLabel(), this.filteredData?.nrOfHighSimilaritySubmissions || 0],
         [this.getMediumRiskLabel(), this.filteredData?.nrOfMediumSimilaritySubmissions || 0],
         [this.getLowRiskLabel(), this.filteredData?.nrOfLowSimilaritySubmissions || 0],
         ["In Process", this.filteredData?.inProcessSubmissions || 0],

      ];

      data.push([""]);
      data.push(["Monthly Similarity Trends", this.selectedYear]);
      data.push([""]);
      data.push(["", "Low Similarity", "Medium Similarity", "High Similarity"]);
      data.push(["Jan", this.riskTrendsData?.lowSimilaritySubmissions?.[0] || 0, this.riskTrendsData?.mediumSimilaritySubmissions?.[0] || 0, this.riskTrendsData?.highSimilaritySubmissions?.[0] || 0]);
      data.push(["Feb", this.riskTrendsData?.lowSimilaritySubmissions?.[1] || 0, this.riskTrendsData?.mediumSimilaritySubmissions?.[1] || 0, this.riskTrendsData?.highSimilaritySubmissions?.[1] || 0]);
      data.push(["Mar", this.riskTrendsData?.lowSimilaritySubmissions?.[2] || 0, this.riskTrendsData?.mediumSimilaritySubmissions?.[2] || 0, this.riskTrendsData?.highSimilaritySubmissions?.[2] || 0]);
      data.push(["Apr", this.riskTrendsData?.lowSimilaritySubmissions?.[3] || 0, this.riskTrendsData?.mediumSimilaritySubmissions?.[3] || 0, this.riskTrendsData?.highSimilaritySubmissions?.[3] || 0]);
      data.push(["May", this.riskTrendsData?.lowSimilaritySubmissions?.[4] || 0, this.riskTrendsData?.mediumSimilaritySubmissions?.[4] || 0, this.riskTrendsData?.highSimilaritySubmissions?.[4] || 0]);
      data.push(["Jun", this.riskTrendsData?.lowSimilaritySubmissions?.[5] || 0, this.riskTrendsData?.mediumSimilaritySubmissions?.[5] || 0, this.riskTrendsData?.highSimilaritySubmissions?.[5] || 0]);
      data.push(["Jul", this.riskTrendsData?.lowSimilaritySubmissions?.[6] || 0, this.riskTrendsData?.mediumSimilaritySubmissions?.[6] || 0, this.riskTrendsData?.highSimilaritySubmissions?.[6] || 0]);
      data.push(["Aug", this.riskTrendsData?.lowSimilaritySubmissions?.[7] || 0, this.riskTrendsData?.mediumSimilaritySubmissions?.[7] || 0, this.riskTrendsData?.highSimilaritySubmissions?.[7] || 0]);
      data.push(["Sep", this.riskTrendsData?.lowSimilaritySubmissions?.[8] || 0, this.riskTrendsData?.mediumSimilaritySubmissions?.[8] || 0, this.riskTrendsData?.highSimilaritySubmissions?.[8] || 0]);
      data.push(["Oct", this.riskTrendsData?.lowSimilaritySubmissions?.[9] || 0, this.riskTrendsData?.mediumSimilaritySubmissions?.[9] || 0, this.riskTrendsData?.highSimilaritySubmissions?.[9] || 0]);
      data.push(["Nov", this.riskTrendsData?.lowSimilaritySubmissions?.[10] || 0, this.riskTrendsData?.mediumSimilaritySubmissions?.[10] || 0, this.riskTrendsData?.highSimilaritySubmissions?.[10] || 0]);
      data.push(["Dec", this.riskTrendsData?.lowSimilaritySubmissions?.[11] || 0, this.riskTrendsData?.mediumSimilaritySubmissions?.[11] || 0, this.riskTrendsData?.highSimilaritySubmissions?.[11] || 0]);

      // Add original language distribution data

      data.push([""]);
      data.push(["Language Distribution - Original"]);
      data.push([""]);

      if (this.languageDistributionData.original?.languages) {
         this.languageDistributionData.original.languages.forEach(lang => {
            data.push([this.getLanguageName(lang.language), lang.count]);
         });
      }

      data.push([""]);
      data.push(["Language Distribution - Translated"]);
      data.push([""]);

      // Add translated language distribution data
      if (this.languageDistributionData.translated?.languages) {
         this.languageDistributionData.translated.languages.forEach(lang => {
            data.push([this.getLanguageName(lang.language), lang.count]);
         });
      }

      data.push([""]);
      data.push(["AI Detection Monthly Trends"]);
      data.push([""]);

      if (this.aiDetectionData?.aiDetection) {
         const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
         months.forEach((month, index) => {
            data.push([month, this.aiDetectionData.aiDetection[index] || 0]);
         });
      }

      data.push([""]);
      data.push(["Manipulation Types"]);
      data.push([""]);

      if (this.manipulationData) {
         data.push(["Character Replacements", this.manipulationData.characterReplacements || 0]);
         data.push(["Hidden Text", this.manipulationData.hiddenText || 0]);
         data.push(["Image as Text", this.manipulationData.imageAsText || 0]);
      }

      data.push([""]);
      let userRegHeader = ["User Registration Trends", this.selectedYear];

      data.push(userRegHeader);
      data.push([""]);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const educators = this.userRegistrationTrendsData?.educators || Array(12).fill(0);
      const students = this.userRegistrationTrendsData?.students || Array(12).fill(0);

      data.push(["", "Educators", "Students"]);
      for (let i = 0; i < 12; i++) {
         data.push([months[i], educators[i], students[i]]);
      }



      return data;
   }

   async exportToCSV() {
      this.spinner.show();
      await this.fetchRiskTrendsForExport();
      const rows = this.prepareExportData();
      let year = 0

      this.statistics.selectedYearObservable.subscribe((data) => {
         year = data
      })
      this.csvExportService.exportToCSV(rows, 'Analytics - Dashboard Overview Report');
      this.spinner.hide()
   }

   async exportToExcel() {
      this.spinner.show()
      await this.fetchRiskTrendsForExport();
      const rows = this.prepareExportData()
      let year = 0

      this.statistics.selectedYearObservable.subscribe((data) => {
         year = data
      })


      this.excelExportService.exportToExcel(rows, 'Analytics - Dashboard Overview Report');
      this.spinner.hide()

   }

   exportToPDF() {
      this.spinner.show()
      if (this.contentToCapture) {
         this.pdfExportService.captureAndExportPDF(this.contentToCapture.nativeElement, 'Analytics - Dashboard Overview Report.pdf');
         this.spinner.hide()
      } else {
         console.error('Content to capture is not available yet.');
         this.spinner.hide()
      }
   }

   aggregateLanguageData() {
      const submissions = this.submissionsStatistics || [];
      console.log('submissions', submissions);
      const originalLangMap = new Map<string, number>();
      const translatedLangMap = new Map<string, number>();
      submissions.forEach(sub => {
         if (sub.originalLanguage) {
            originalLangMap.set(sub.originalLanguage, (originalLangMap.get(sub.originalLanguage) || 0) + 1);
         }
         if (sub.translatedLanguage) {
            translatedLangMap.set(sub.translatedLanguage, (translatedLangMap.get(sub.translatedLanguage) || 0) + 1);
         }
      });
      this.originalLanguageData = Array.from(originalLangMap.entries()).map(([label, value], i) => ({
         label,
         value,
         color: this.languageColors[i % this.languageColors.length]
      }));
      this.translatedLanguageData = Array.from(translatedLangMap.entries()).map(([label, value], i) => ({
         label,
         value,
         color: this.languageColors[i % this.languageColors.length]
      }));
      this.originalLanguageTotal = this.originalLanguageData.reduce((sum, d) => sum + d.value, 0);
      this.translatedLanguageTotal = this.translatedLanguageData.reduce((sum, d) => sum + d.value, 0);
   }

   fetchAllDashboardData() {
      const year = this.selectedYear || new Date().getFullYear();
      const filterType = this.selectedSubmissionType || 'student';
      this.submissionsService.getLanguageDistribution(year.toString(), 'original', false, filterType).subscribe(
         response => {
            this.languageDistributionData.original = response;
         },
         error => {
            this.languageDistributionData.original = {};
         }
      );
      this.submissionsService.getLanguageDistribution(year.toString(), 'translated', false, filterType).subscribe(
         response => {
            this.languageDistributionData.translated = response;
         },
         error => {
            this.languageDistributionData.translated = {};
         }
      );
      this.submissionsService.getAiDetectionSubmissions(year.toString(), false, undefined, undefined, undefined, filterType).subscribe(
         response => {
            this.aiDetectionData = response;
         },
         error => {
            this.aiDetectionData = {};
         }
      );
      this.submissionsService.getManipulationSubmissions(year.toString(), false, undefined, undefined, undefined, filterType).subscribe(
         response => {
            this.manipulationData = response;
         },
         error => {
            this.manipulationData = {};
         }
      );
      this.statistics.institutionSubmissionsStatistics(year).pipe(first()).subscribe(
         data => {
            this.riskTrendsData = data;
         },
         error => {
            this.riskTrendsData = {};
         }
      );
      this.statistics.userRegistrationTrends(year, "all").pipe(first()).subscribe(
         data => {
            this.userRegistrationTrendsData = data;
         },
         error => {
            this.userRegistrationTrendsData = {};
         }
      );
   }

   async fetchRiskTrendsForExport(): Promise<any> {
      const year = this.selectedYear || new Date().getFullYear();
      const type = this.selectedSubmissionType || 'student';
      let obs;
      if (type === 'student') {
         obs = this.statistics.institutionSubmissionsStatistics(year);
      } else {
         obs = this.statistics.institutionSubmissionsStatisticsBasic(year);
      }
      return new Promise((resolve, reject) => {
         obs.pipe(first()).subscribe(
            data => {
               this.riskTrendsData = {
                  lowSimilaritySubmissions: data.lowSimilarity || Array(12).fill(0),
                  mediumSimilaritySubmissions: data.mediumSimilarity || Array(12).fill(0),
                  highSimilaritySubmissions: data.highSimilarity || Array(12).fill(0)
               };
               resolve(this.riskTrendsData);
            },
            error => {
               this.riskTrendsData = {
                  lowSimilaritySubmissions: Array(12).fill(0),
                  mediumSimilaritySubmissions: Array(12).fill(0),
                  highSimilaritySubmissions: Array(12).fill(0)
               };
               resolve(this.riskTrendsData);
            }
         );
      });
   }
   /**
    * Get dynamic label for high risk submissions
    */
   getHighRiskLabel(): string {
      if (this.userInterfaceDisplay === 'riskFactor') {
         return this.translate.instant('new_entries.high_risk_submissions');
      } else {
         return this.translate.instant('new_entries.submission_from') + ` ${this.originalityRiskFactorHighRisk}% - 100%`;
      }
   }

   /**
    * Get dynamic label for medium risk submissions
    */
   getMediumRiskLabel(): string {
      if (this.userInterfaceDisplay === 'riskFactor') {
         return this.translate.instant('new_entries.medium_risk_submissions');
      } else {
         return this.translate.instant('new_entries.submission_from') + ` ${this.originalityRiskFactorMediumRisk}% - ${this.originalityRiskFactorHighRisk}%`;
      }
   }

   /**
    * Get dynamic label for low risk submissions
    */
   getLowRiskLabel(): string {
      if (this.userInterfaceDisplay === 'riskFactor') {
         return this.translate.instant('new_entries.low_risk_submissions');
      } else {
         return this.translate.instant('new_entries.submission_from') + ` 0% - ${this.originalityRiskFactorMediumRisk}%`;
      }
   }

   getLanguageName(code: any): string {
      if (!code) return '';
      let value = code;
      let safety = 0;
      while (Array.isArray(value) && value.length === 1 && safety < 10) {
         value = value[0];
         safety++;
      }
      safety = 0;
      while (typeof value === 'string' && safety < 10) {
         let trimmed = value.trim();
         while ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
            trimmed = trimmed.slice(1, -1);
         }
         trimmed = trimmed.replace(/\\"/g, '"');
         if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
            try {
               const parsed = JSON.parse(trimmed);
               if (Array.isArray(parsed)) {
                  value = parsed;
                  break;
               } else {
                  value = parsed;
               }
            } catch (e) {
               value = trimmed;
               break;
            }
         } else {
            value = trimmed;
            break;
         }
         safety++;
      }
      if (Array.isArray(value)) {
         if (value.length === 0) return 'None';
         return value.map(lang => this.languageMap[lang.toLowerCase()] || lang).join(', ');
      }
      if (typeof value === 'string') {
         const normalizedCode = value.toLowerCase();
         return this.languageMap[normalizedCode] || value;
      }
      return '';
   }
}
